import * as React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { style } from "typestyle";
import { typography } from "../typography.js";
import { layout } from "../layout.js";
import { classNames, toT } from "../utils.js";
import { KeyPad } from "./KeyPad.js";
import { TextInput } from "./TextInput.js";
import { PushButton } from "../PushButton.js";
import { useMounted } from "../hooks/useMounted.js";
import { useAsyncEffect } from "../hooks/async.js";
import { Keyboard } from "./Keyboard.js";
import { useCountdown } from "../hooks/countdown.js";
const FAILURE_THRESHOLD = 4;
export function CodeForm(props) {
    const { length, onVerify, onCancel, strings, className, input = { type: "keypad" }, revealInput = false, } = props;
    const t = toT(strings);
    const mounted = useMounted();
    const [code, setCode] = useState("");
    const [pending, setPending] = useState(false);
    const [error, setError] = useState();
    const [failedAttempts, setFailedAttempts] = useState(0);
    const secondsLeft = useCountdown(Math.min(failedAttempts, 10), failedAttempts > FAILURE_THRESHOLD);
    const verify = useCallback(async () => {
        if (code) {
            try {
                setPending(true);
                await onVerify(code);
                if (mounted()) {
                    setError(undefined);
                    setPending(false);
                    setCode("");
                    resetTimePenalty();
                }
            }
            catch (error) {
                if (mounted()) {
                    setError(error);
                    setPending(false);
                    setCode("");
                    pushFailedAttempts();
                }
            }
        }
    }, [onVerify, code]);
    function pushFailedAttempts() {
        setFailedAttempts(prev => prev + 1);
    }
    function resetTimePenalty() {
        setFailedAttempts(0);
    }
    const handleType = useCallback((key) => setCode(prev => prev + key), []);
    useAsyncEffect(async () => {
        if (length && length == code.length) {
            await verify();
        }
    }, [length, code, verify]);
    useEffect(() => {
        if (code) {
            setError(undefined);
        }
    }, [code]);
    const { message, className: messageClassName } = useMemo(() => {
        if (error) {
            return {
                message: t("InvalidCode"),
                className: typography.colorError,
            };
        }
        else if (pending) {
            return {
                message: t("CheckingCode"),
                className: undefined,
            };
        }
        else if (length && code.length > 0 && code.length < length) {
            return {
                message: t("MoreDigitsRequired"),
                className: typography.muted,
            };
        }
        else {
            return {
                message: t("PleaseEnterCode"),
                className: typography.muted,
            };
        }
    }, [error, pending, length, code, strings]);
    const inputDisabled = pending || secondsLeft > 0;
    function renderLockSeconds() {
        return (React.createElement("p", { className: typography.colorError, style: { marginTop: 0 } },
            React.createElement("span", null, t('PenaltyDuration', { secondsLeft }))));
    }
    return (React.createElement("div", { className: classNames(input.type == "keyboard" ? layout.vertical : layout.horizontal, layout.spaceEvenly, className) },
        React.createElement("div", { className: classNames(input.type == "keyboard" ? layout.horizontal : layout.vertical, layout.center, typography.alignCenter) },
            React.createElement("div", { className: classNames(layout.vertical, codeInputWidth) },
                React.createElement(TextInput, { value: code, onChange: setCode, type: revealInput ? "text" : "password", disabled: inputDisabled, outline: true }),
                React.createElement("p", { className: messageClassName }, message),
                secondsLeft > 0 && renderLockSeconds()),
            !length && (React.createElement("div", { className: button },
                React.createElement(PushButton, { primary: true, onClick: verify, disabled: secondsLeft > 0 }, t("Submit")))),
            onCancel && (React.createElement("div", { className: button },
                React.createElement(PushButton, { onClick: onCancel }, t("Cancel"))))),
        input.type == "keypad" && (React.createElement("div", { className: classNames(layout.vertical, layout.center) },
            React.createElement(KeyPad, { primary: true, round: true, size: 2.5, onType: handleType, disabled: inputDisabled, digits: input.digits }))),
        input.type == "keyboard" && (React.createElement(Keyboard, { layout: input.layout, onType: handleType, disabled: inputDisabled }))));
}
const codeInputWidth = style({
    width: "20em",
});
const button = style({
    margin: "0.5em",
    textAlign: "center",
});
//# sourceMappingURL=CodeForm.js.map